import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Stack, TextField, IconButton, Tooltip, Alert, CircularProgress } from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Panel, field } from './uiKit';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import http, { apiErrorMessage } from '../Api/http';
import { GetEmployeeAttendanceLeaveSummary } from '../Api/Api';

const pad = (n) => String(n).padStart(2, '0');
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const isoMonthStart = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };
const toDMY = (iso) => { const [y, m, d] = String(iso).split('-'); return d ? `${d}-${m}-${y}` : ''; };

// camelCase key -> "Camel case"
const prettyKey = (k) => String(k).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
const isPercentKey = (k) => /percent/i.test(k);
const SKIP = new Set(['employeeCode', 'employeeName', 'name', 'fromDate', 'toDate', 'financialYear']);

// Flatten the summary into label/value tiles: top-level scalars, plus any one-level object
// (leaveSummary) as its own group. The exact keys aren't pinned down, so whatever comes back shows.
const tilesOf = (d) => {
    const out = [];
    Object.entries(d || {}).forEach(([k, v]) => {
        if (SKIP.has(k) || v == null) return;
        if (typeof v === 'object' && !Array.isArray(v)) {
            Object.entries(v).forEach(([k2, v2]) => {
                if (v2 != null && typeof v2 !== 'object') out.push({ key: `${k}.${k2}`, label: prettyKey(k2), group: prettyKey(k), value: v2, pct: isPercentKey(k2) });
            });
        } else if (typeof v !== 'object') {
            out.push({ key: k, label: prettyKey(k), group: '', value: v, pct: isPercentKey(k) });
        }
    });
    return out;
};

// One employee's attendance counts + leave totals over a date range (GetEmployeeAttendanceLeaveSummary).
export default function EmployeeAttendanceSummary({ employeeCode }) {
    const [from, setFrom] = useState(isoMonthStart());
    const [to, setTo] = useState(isoToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!employeeCode || !from || !to) return;
        if (to < from) { setError('The end date can’t be before the start date.'); setData(null); return; }
        setLoading(true);
        setError('');
        try {
            const { data: body } = await http.get(GetEmployeeAttendanceLeaveSummary, {
                params: { employeeCode, fromDate: toDMY(from), toDate: toDMY(to) },
            });
            if (body?.error) throw new Error(body.message || 'Could not load the summary.');
            setData(body?.data ?? null);
        } catch (err) {
            setData(null);
            if (err?.response?.status === 404) setError('No attendance or leave on record for this period.');
            else setError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load the summary.') : (err.message || 'Could not load the summary.'));
        } finally {
            setLoading(false);
        }
    }, [employeeCode, from, to]);

    useEffect(() => { load(); }, [load]);

    const tiles = tilesOf(data);

    return (
        <Panel
            title="Attendance & Leave Summary"
            icon={EventAvailableRoundedIcon}
            action={(
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <TextField type="date" size="small" label="From" value={from} onChange={(e) => setFrom(e.target.value)} sx={{ ...field, width: 150 }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || undefined } }} />
                    <TextField type="date" size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)} sx={{ ...field, width: 150 }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }} />
                    <Tooltip arrow title="Reload">
                        <span>
                            <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            )}
        >
            <Box sx={{ p: 1.8 }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : error ? (
                    <Alert severity="info" sx={{ borderRadius: '9px' }}>{error}</Alert>
                ) : tiles.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#98A0AE', textAlign: 'center', py: 3 }}>Nothing to show for this period.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.2 }}>
                        {tiles.map((t) => (
                            <Box key={t.key} sx={{ p: 1.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                    {t.group ? `${t.group} · ${t.label}` : t.label}
                                </Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A', mt: 0.3 }}>
                                    {t.pct && Number.isFinite(Number(t.value)) ? `${Number(t.value).toFixed(1).replace(/\.0$/, '')}%` : String(t.value)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Panel>
    );
}
